import axios from 'axios';
import { API_URL } from '../config/api';

/**
 * Pipeline Service
 * 
 * What this does:
 * - Makes API calls to the backend for pipeline operations
 * - Handles all HTTP requests related to pipelines
 * 
 * Usage in components:
 * import pipelineService from '../services/pipelineService';
 * const pipeline = await pipelineService.getDetailed(pipelineId);
 */

const API_BASE = API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE
});

// Add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const pipelineService = {
  
  /**
   * Create new pipeline
   */
  create: async (pipelineData) => {
    const response = await api.post('/pipelines', pipelineData);
    return response.data;
  },

  /**
   * Get all active pipelines
   */
  getAll: async () => {
    const response = await api.get('/pipelines');
    return response.data;
  },

  /**
   * Get pipelines for a firm
   */
  getByFirm: async (firmId) => {
    const response = await api.get(`/firms/${firmId}/pipelines`);
    return response.data;
  },

  /**
   * Get pipeline with full board data (for Kanban)
   */
  getDetailed: async (pipelineId) => {
    const response = await api.get(`/pipelines/${pipelineId}/detailed`);
    return response.data;
  },

  /**
   * Get basic pipeline info
   */
  getById: async (pipelineId) => {
    const response = await api.get(`/pipelines/${pipelineId}`);
    return response.data;
  },

  /**
   * Update pipeline
   */
  update: async (pipelineId, updates) => {
    const response = await api.put(`/pipelines/${pipelineId}`, updates);
    return response.data;
  },

  /**
   * Archive pipeline
   */
  archive: async (pipelineId) => {
    const response = await api.post(`/pipelines/${pipelineId}/archive`);
    return response.data;
  },

  /**
   * Delete pipeline
   */
  delete: async (pipelineId) => {
    await api.delete(`/pipelines/${pipelineId}`);
  },

  /**
   * Get pipeline statistics
   */
  getStatistics: async (pipelineId) => {
    const response = await api.get(`/pipelines/${pipelineId}/statistics`);
    return response.data;
  },

  // STAGE OPERATIONS

  /**
   * Add custom internal stage
   */
  addStage: async (pipelineId, stageData) => {
    const response = await api.post(`/pipelines/${pipelineId}/stages`, stageData);
    return response.data;
  },

  /**
   * Update stage
   */
  updateStage: async (stageId, updates) => {
    const response = await api.put(`/stages/${stageId}`, updates);
    return response.data;
  },

  /**
   * Delete stage
   */
  deleteStage: async (stageId) => {
    await api.delete(`/stages/${stageId}`);
  },

  /**
   * Reorder stages
   */
  reorderStages: async (pipelineId, stageOrder) => {
    const response = await api.put(
      `/pipelines/${pipelineId}/stages/reorder`,
      { stageOrder }
    );
    return response.data;
  },

  /**
   * Add client interview stage
   */
  addClientStage: async (pipelineId, stageData) => {
    const response = await api.post(
      `/pipelines/${pipelineId}/client-stages`,
      stageData
    );
    return response.data;
  },

  /**
   * Update client interview stage
   */
  updateClientStage: async (stageId, updates) => {
    const response = await api.put(`/stages/client/${stageId}`, updates);
    return response.data;
  },

  /**
   * Delete client interview stage
   */
  deleteClientStage: async (stageId) => {
    await api.delete(`/stages/client/${stageId}`);
  },

  // CARD OPERATIONS

  /**
   * Get card details
   */
  getCard: async (cardId) => {
    const response = await api.get(`/cards/${cardId}`);
    return response.data;
  },

  /**
   * Move card to different stage
   */
  moveCard: async (cardId, stageId) => {
    const response = await api.put(`/cards/${cardId}/move`, { stage_id: stageId });
    return response.data;
  },

  /**
   * Update card position
   */
  updateCardPosition: async (cardId, position) => {
    const response = await api.put(`/cards/${cardId}/position`, { position });
    return response.data;
  },

  /**
   * Batch update card positions
   */
  batchUpdatePositions: async (updates) => {
    const response = await api.put(`/cards/batch-positions`, { updates });
    return response.data;
  },

  /**
   * Update card priority
   */
  updateCardPriority: async (cardId, priority) => {
    const response = await api.put(`/cards/${cardId}/priority`, { priority });
    return response.data;
  },

  /**
   * Delete card
   */
  deleteCard: async (cardId) => {
    await api.delete(`/cards/${cardId}`);
  },

  /**
   * Assign user to card
   */
  assignUser: async (cardId, userId) => {
    const response = await api.post(`/cards/${cardId}/assign`, { user_id: userId });
    return response.data;
  },

  /**
   * Unassign user from card
   */
  unassignUser: async (cardId, userId) => {
    await api.delete(`/cards/${cardId}/assign/${userId}`);
  },

  /**
   * Get assigned users
   */
  getAssignedUsers: async (cardId) => {
    const response = await api.get(`/cards/${cardId}/assigned-users`);
    return response.data;
  },

  /**
   * Get chat messages
   */
  getChatMessages: async (cardId) => {
    const response = await api.get(`/cards/${cardId}/chat`);
    return response.data;
  },

  /**
   * Add chat message
   */
  addChatMessage: async (cardId, message, mentionedUsers = []) => {
    const response = await api.post(`/cards/${cardId}/chat`, {
      message,
      mentioned_users: mentionedUsers
    });
    return response.data;
  },

  /**
   * Update chat message
   */
  updateChatMessage: async (chatId, message) => {
    const response = await api.put(`/cards/chat/${chatId}`, { message });
    return response.data;
  },

  /**
   * Delete chat message
   */
  deleteChatMessage: async (chatId) => {
    await api.delete(`/cards/chat/${chatId}`);
  },

  /**
   * Get documents
   */
  getDocuments: async (cardId) => {
    const response = await api.get(`/cards/${cardId}/documents`);
    return response.data;
  },

  /**
   * Upload document
   */
  uploadDocument: async (cardId, file, fileType, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    if (notes) formData.append('notes', notes);

    const response = await api.post(
      `/cards/${cardId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );
    return response.data;
  },

  /**
   * Delete document
   */
  deleteDocument: async (documentId) => {
    await api.delete(`/cards/documents/${documentId}`);
  },

  /**
   * Get activity timeline
   */
  getActivities: async (cardId) => {
    const response = await api.get(`/cards/${cardId}/activities`);
    return response.data;
  }
};

export default pipelineService;